// Free Shipping + Free Gifts Bar
var FreeShippingBar = class extends HTMLElement {
  static get observedAttributes() {
    return ["threshold", "total-price", "first-gift-threshold", "second-gift-threshold"];
  }

  constructor() {
    super();
    this._onCartChangedListener = this._onCartChanged.bind(this);
    this._currencyFormatter = new Intl.NumberFormat(Shopify.locale, {
      style: "currency",
      currency: Shopify.currency.active
    });
    // Initialize with a default total price of 0
    this.totalPrice = 0;
  }

  async connectedCallback() {
    // Normalize thresholds to current currency
    this.threshold = Math.round(this.getAttribute("threshold") * (Shopify.currency.rate || 1));
    this.firstGiftThreshold = Math.round(this.getAttribute("first-gift-threshold") * (Shopify.currency.rate || 1));
    this.secondGiftThreshold = Math.round(this.getAttribute("second-gift-threshold") * (Shopify.currency.rate || 1));

    // Listen to Impulse cart events
    document.addEventListener("cart:updated", this._onCartChangedListener);
    // (Optional fallback if other apps use cart:change)
    document.addEventListener("cart:change", this._onCartChangedListener);

    // *** NEW: Fetch initial cart data on load ***
    await this.fetchAndUpdateCart();

    this._updateMessage();
  }

  disconnectedCallback() {
    document.removeEventListener("cart:updated", this._onCartChangedListener);
    document.removeEventListener("cart:change", this._onCartChangedListener);
  }

  get threshold() {
    return parseFloat(this.getAttribute("threshold"));
  }
  set threshold(value) {
    this.setAttribute("threshold", value);
  }

  get firstGiftThreshold() {
    return parseFloat(this.getAttribute("first-gift-threshold"));
  }
  set firstGiftThreshold(value) {
    this.setAttribute("first-gift-threshold", value);
  }

  get secondGiftThreshold() {
    return parseFloat(this.getAttribute("second-gift-threshold"));
  }
  set secondGiftThreshold(value) {
    this.setAttribute("second-gift-threshold", value);
  }

  get totalPrice() {
    return parseFloat(this.getAttribute("total-price"));
  }
  set totalPrice(value) {
    this.setAttribute("total-price", value);
  }

  async attributeChangedCallback(attribute, oldValue, newValue) {
    await window.customElements.whenDefined("progress-bar");
    const progressBarElement = this.querySelector("progress-bar");

    if (attribute === "threshold") {
      // Ensure valueMax is updated if thresholds change dynamically
      progressBarElement.valueMax = this.secondGiftThreshold;
    }

    if (attribute === "total-price") {
      progressBarElement.valueNow = newValue;
    }

    this._updateMessage();
  }

  async fetchAndUpdateCart() {
    // Check if theme.cart.getCart is available
    if (typeof theme !== 'undefined' && typeof theme.cart !== 'undefined' && typeof theme.cart.getCart === 'function') {
      try {
        const cartData = await theme.cart.getCart();
        this._processCartData(cartData);
      } catch (error) {
        console.error("Error fetching cart data:", error);
        // Fallback to current totalPrice if fetch fails
        this._updateMessage();
      }
    } else {
      // If theme.cart.getCart is not available, assume totalPrice is already set or 0
      this._updateMessage();
    }
  }

  _processCartData(cart) {
    const priceForItems = cart.items
      // .filter(item => item.requires_shipping)
      .reduce((sum, item) => sum + item.final_line_price, 0);

    const cartDiscount = cart.cart_level_discount_applications && cart.cart_level_discount_applications.length
      ? cart.cart_level_discount_applications.reduce((sum, discountAllocation) => sum + discountAllocation.total_allocated_amount, 0)
      : 0;

    this.totalPrice = priceForItems - cartDiscount;
    // Also update the progress bar element if it exists
    if (this.querySelector('progress-bar')) {
        this.querySelector('progress-bar').valueNow = this.totalPrice;
    }
    this._updateMessage();
  }

  _updateMessage() {
    const currency = this._currencyFormatter;
    const msgEl = this.querySelector(".progress-message");

    // Ensure thresholds are numbers before calculations
    const currentThreshold = parseFloat(this.threshold);
    const currentFirstGiftThreshold = parseFloat(this.firstGiftThreshold);
    const currentSecondGiftThreshold = parseFloat(this.secondGiftThreshold);
    const currentTotalPrice = parseFloat(this.totalPrice);


    const remainingShipping = currentThreshold - currentTotalPrice;
    const remainingFirstGift = currentFirstGiftThreshold - currentTotalPrice;
    const remainingSecondGift = currentSecondGiftThreshold - currentTotalPrice;

    const replacements = {
      // Use Math.max(0, ...) to prevent showing negative amounts needed
      "{shipping}": currency.format(Math.max(0, remainingShipping) / 100),
      "{gift1}": currency.format(Math.max(0, remainingFirstGift) / 100),
      "{gift2}": currency.format(Math.max(0, remainingSecondGift) / 100)
    };

    let message = "";

    if (currentTotalPrice >= currentSecondGiftThreshold) {
      message = this.getAttribute("msg-gift2");
    } else if (currentTotalPrice >= currentFirstGiftThreshold) {
      message = this.getAttribute("msg-gift1");
    } else if (currentTotalPrice >= currentThreshold) {
      message = this.getAttribute("msg-shipping-unlocked");
    } else {
      message = this.getAttribute("msg-below-shipping");
    }

    // Replace placeholders
    Object.keys(replacements).forEach(key => {
      message = message.replaceAll(key, replacements[key]);
    });

    if (msgEl) {
        msgEl.innerHTML = message;
    }
    this._updateMarkers();
  }

  _updateMarkers() {
    const markers = this.querySelectorAll(".progress-marker");
    // Ensure we don't divide by zero if secondGiftThreshold is 0
    const maxThreshold = this.secondGiftThreshold > 0 ? this.secondGiftThreshold : (this.firstGiftThreshold > 0 ? this.firstGiftThreshold : this.threshold);
    const progressPercent = maxThreshold > 0 ? (this.totalPrice / maxThreshold) * 100 : 0;


    markers.forEach(marker => {
      const posAttr = marker.getAttribute('style');
      // Safely parse the --pos value
      const posMatch = posAttr ? posAttr.match(/--pos: ([^;%]+)/) : null;
      const pos = posMatch ? parseFloat(posMatch[1]) : 0;

      if (progressPercent >= pos) {
        marker.classList.add("reached");
      } else {
        marker.classList.remove("reached");
      }
    });
  }

  _onCartChanged(event) {
    // Ensure event.detail.cart exists before processing
    if (event.detail && event.detail.cart) {
      this._processCartData(event.detail.cart);
    } else {
        // If the event structure is different, try fetching the cart directly
        this.fetchAndUpdateCart();
    }
  }
};

if (!window.customElements.get("free-shipping-bar")) {
  window.customElements.define("free-shipping-bar", FreeShippingBar);
}

// Progress bar component
var ProgressBar = class extends HTMLElement {
  static get observedAttributes() {
    return ["aria-valuenow", "aria-valuemax"];
  }
  set valueMax(value) {
    this.setAttribute("aria-valuemax", value);
  }
  set valueNow(value) {
    this.setAttribute("aria-valuenow", value);
  }
  attributeChangedCallback() {
    const valNow = parseFloat(this.getAttribute("aria-valuenow"));
    const valMax = parseFloat(this.getAttribute("aria-valuemax"));

    if (!isNaN(valNow) && !isNaN(valMax) && valMax > 0) {
      this.style.setProperty(
        "--progress",
        `${Math.min(1, valNow / valMax)}`
      );
    } else {
      // Handle cases where values might be invalid or valMax is 0
      this.style.setProperty("--progress", "0");
    }
  }
};
if (!window.customElements.get("progress-bar")) {
  window.customElements.define("progress-bar", ProgressBar);
}